import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Search, Loader2, X } from "lucide-react";

const GARMENTS = [
  { key: "all", name: "All" },
  { key: "hoodie", name: "Hoodies" },
  { key: "shirt", name: "Shirts" },
];

const SORTS = [
  { key: "featured", name: "Featured" },
  { key: "price-asc", name: "Price: Low to High" },
  { key: "price-desc", name: "Price: High to Low" },
  { key: "name-asc", name: "Name: A to Z" },
];

export default function ShopSection() {
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all");
  const [search, setSearch] = useState("");
  const [garment, setGarment] = useState("all");
  const [sort, setSort] = useState("featured");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState({});
  const [searchParams] = useSearchParams();

  // Deep-link from homepage league strips: /?cat=nfl#shop
  useEffect(() => {
    const cat = searchParams.get("cat");
    if (cat) {
      setActive(cat);
      setTimeout(() => document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, [searchParams]);

  useEffect(() => {
    api.get("/reviews/summary").then((r) => setRatings(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (active !== "all") params.category = active;
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/products", { params });
      setProducts(r.data);
    } finally {
      setLoading(false);
    }
  }, [active, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const priceOf = (p) => p.display_price ?? Math.min(...(p.variants?.map((v) => v.price) || [0]));

  // A product matches a garment if it has ANY enabled variant of that garment.
  // (Every product is offered as both a hoodie and a shirt, so filtering by
  // display_price alone wrongly hid products — e.g. Champions + Shirts = empty.)
  const hasGarment = (p, g) =>
    (p.variants || []).some(
      (v) => v.is_enabled && (v.options?.garment || "").toLowerCase().includes(g)
    );

  const visible = useMemo(() => {
    let list = [...products];
    if (garment !== "all") {
      list = list.filter((p) => hasGarment(p, garment));
    }
    switch (sort) {
      case "price-asc":
        list.sort((a, b) => priceOf(a) - priceOf(b));
        break;
      case "price-desc":
        list.sort((a, b) => priceOf(b) - priceOf(a));
        break;
      case "name-asc":
        list.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default:
        list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }
    return list;
  }, [products, garment, sort]);

  const hasFilters = active !== "all" || search.trim() || garment !== "all";

  const clearAll = () => {
    setActive("all");
    setSearch("");
    setGarment("all");
    setSort("featured");
  };

  return (
    <section id="shop" data-testid="shop-section" className="px-5 sm:px-6 md:px-12 lg:px-16 py-16 md:py-32 bg-background">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 md:gap-8 mb-10 md:mb-12">
        <div>
          <p className="text-xs uppercase tracking-[0.35em] font-bold text-primary mb-4">The Collection</p>
          <h2 className="font-anton text-4xl sm:text-6xl lg:text-7xl uppercase tracking-tight text-white leading-none">
            Shop the drop
          </h2>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search jerseys, teams, drops..."
            className="bg-transparent border-b-2 border-white/15 focus:border-primary outline-none py-3 pl-7 pr-8 w-full text-sm text-white placeholder:text-white/40 transition-colors"
          />
          {search && (
            <button
              data-testid="clear-search"
              onClick={() => setSearch("")}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-white/40 hover:text-primary transition-colors"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Category pills */}
      <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 mb-6">
        {[{ slug: "all", name: "All" }, ...categories].map((c) => (
          <button
            key={c.slug}
            data-testid={`category-${c.slug}`}
            onClick={() => setActive(c.slug)}
            className={`px-6 py-2.5 text-xs font-bold uppercase tracking-[0.2em] whitespace-nowrap rounded-full border transition-colors ${
              active === c.slug
                ? "bg-primary text-black border-primary"
                : "bg-transparent text-white/70 border-white/20 hover:border-primary hover:text-primary"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Garment filter + sort */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex gap-2" data-testid="garment-filter">
          {GARMENTS.map((g) => (
            <button
              key={g.key}
              data-testid={`garment-${g.key}`}
              onClick={() => setGarment(g.key)}
              className={`px-4 py-2 text-[11px] font-bold uppercase tracking-[0.2em] border transition-colors ${
                garment === g.key
                  ? "bg-white text-black border-white"
                  : "bg-transparent text-white/60 border-white/15 hover:border-white/50 hover:text-white"
              }`}
            >
              {g.name}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <label htmlFor="sort" className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">
            Sort
          </label>
          <select
            id="sort"
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-neutral-900 border border-white/15 text-white text-xs font-bold uppercase tracking-[0.15em] py-2.5 px-3 outline-none focus:border-primary transition-colors cursor-pointer"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key} className="bg-neutral-900">
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Result count */}
      <div className="flex items-center justify-between mb-8">
        <p data-testid="result-count" className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/40">
          {loading ? "Loading…" : `${visible.length} ${visible.length === 1 ? "drop" : "drops"}`}
        </p>
        {hasFilters && (
          <button
            data-testid="clear-filters"
            onClick={clearAll}
            className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary hover:text-white transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-24">
          <p data-testid="no-products" className="text-white/40 font-medium uppercase tracking-[0.2em] mb-6">
            No products found.
          </p>
          {hasFilters && (
            <button
              data-testid="reset-empty"
              onClick={clearAll}
              className="px-6 py-2.5 text-xs font-bold uppercase tracking-[0.2em] bg-primary text-black hover:bg-white transition-colors"
            >
              Reset filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 md:gap-8">
          {visible.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} rating={ratings[p.id]} garmentFilter={garment !== "all" ? garment : undefined} />
          ))}
        </div>
      )}
    </section>
  );
}
